
  import { defineComponent, inject } from "vue";

  import BaseProcessComponentV2 from "@/components/processComponents/v2/BaseProcessComponentV2.vue";
  import VueDatePicker from "@vuepic/vue-datepicker";
  import "@vuepic/vue-datepicker/dist/main.css";
  import { findKey, get, has, isString, set } from "lodash";

  interface Delta {
    start: DateOffset;
    end: DateOffset;
  }

  interface DateOffset {
    year: number;
    month: number;
    day: number;
  }

  export enum PickerType {
    FULL = "full",
    MONTH_YEAR = "month_year",
    YEAR = "year",
  }

  interface DateTimeFormatSettings {
    locale: string;
    options: Intl.DateTimeFormatOptions;
  }

  interface Formatter {
    replace: {
      pattern: string;
      flags: string;
      value: string;
    };
  }

  export default defineComponent({
    name: "SimpleDateV2",

    extends: BaseProcessComponentV2,

    components: {
      "date-picker": VueDatePicker,
    },

    setup(props: any) {
      const root: string = props.path.split(".")[0];

      return {
        [root]: inject(root),
      };
    },

    computed: {
      pickerType(): PickerType {
        if (get(this, "settings.attributes.month-picker", false)) {
          return PickerType.MONTH_YEAR;
        } else if (get(this, "settings.attributes.year-picker", false)) {
          return PickerType.YEAR;
        }

        return PickerType.FULL;
      },
    },

    created() {
      if (this.value) {
        this.internalDate = this.formatFromValue(String(this.value));
      }

      if (has(this.attributes, "delta")) {
        const { minDate, maxDate, range } = this.processDelta(get(this, "settings.attributes.delta") as any);

        set(this, "settings.attributes.year-range", range);

        if (this.pickerType !== PickerType.YEAR) {
          set(this, "settings.attributes.min-date", minDate);
          set(this, "settings.attributes.max-date", maxDate);
        }
      }
    },

    data() {
      return {
        internalDate: null as any,
      };
    },

    methods: {
      handleCleared() {
        (this.value as any) = null;
      },

      setFormattedValue(value: Date | string) {
        let formattedDate = null;

        if (isString(value)) {
          formattedDate = this.format(value);
        } else {
          const dateTimeFormat: DateTimeFormatSettings = get(this, "settings.dateTimeFormat") as DateTimeFormatSettings;
          formattedDate = this.format(
            new Intl.DateTimeFormat(dateTimeFormat.locale, dateTimeFormat.options).format(value),
          );
        }

        if (this.attributes.return_type === "number") {
          set(this, "value", Number(formattedDate));
        } else {
          set(this, "value", formattedDate);
        }

        return formattedDate;
      },

      format(value: string): string {
        if (has(this.settings, "formatter")) {
          const formatter: Formatter = get(this, "settings.formatter") as Formatter;

          if (formatter.replace?.pattern) {
            value = value.replace(
              new RegExp(formatter.replace.pattern, formatter.replace.flags ?? "g"),
              formatter.replace.value ?? ""
            );
          }

          if (get(this, "settings.formatter.textTransform", null) === "uppercase") {
            value = value.toUpperCase();
          }

          const monthsMapper = get(this, "settings.formatter.monthsMapper", {}) as Record<string, any>;

          if (get(this, "settings.dateTimeFormat.locale") === "es-ES") {
            const replaceValue: string = get(this, "settings.formatter.replace.value", "") as string;
            const [day, monthText, year] = value.split(replaceValue);
            const month = monthsMapper[monthText] ? monthsMapper[monthText] : monthText;

            if (this.pickerType === PickerType.FULL) {
              value = [day, month, year].join(replaceValue);
            } else if (this.pickerType === PickerType.MONTH_YEAR) {
              value = [month, year].join(replaceValue);
            } else {
              value = year;
            }
          }
        }

        return value;
      },

      formatFromValue(value: string) {
        let formattedValue: Date | string | Record<string, any> = value;

        const monthsMapper = get(this, "settings.formatter.monthsMapper", {});

        if (get(this, "settings.dateTimeFormat.locale") === "es-ES") {
          const replaceValue: string = get(this, "settings.formatter.replace.value", "") as string;
          if (this.pickerType === PickerType.FULL) {
            const [day, monthText, year] = value.split(replaceValue);
            const month = findKey(monthsMapper, (value: string) => monthText === value);

            formattedValue = new Date(`${month}-${day}-${year}`);
          } else if (this.pickerType === PickerType.MONTH_YEAR) {
            const [monthText, year] = value.split(replaceValue);

            const month = findKey(monthsMapper, (value: string) => monthText === value);

            formattedValue = `${month}-${year}`;
          } else if (this.pickerType === PickerType.YEAR) {
            const [year] = value.split(replaceValue);
            formattedValue = year;
          }
        }

        return formattedValue;
      },

      processDelta(delta: Delta): { minDate: Date; maxDate: Date; range: number[] } {
        const applyDelta = ({
          base = new Date(),
          delta = { year: 0, month: 0, day: 0 },
        }: {
          base: Date;
          delta: DateOffset;
        }): Date => {
          const date = new Date();

          date.setFullYear(base.getFullYear() + delta.year);
          date.setMonth(base.getMonth() + delta.month);
          date.setDate(base.getDate() + delta.day);

          return date;
        };

        const minDate = applyDelta({ base: new Date(), delta: delta.start });
        const maxDate = applyDelta({ base: new Date(), delta: delta.end });

        return {
          minDate: minDate,
          maxDate: maxDate,
          range: [minDate.getFullYear(), maxDate.getFullYear()],
        };
      },
    },
  });
